import { useState, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import toast from 'react-hot-toast';
import { MapView } from './MapView';
import { DataTable } from './DataTable';
import type { VarietyRecord } from './types';

const SAMPLE_FILES = [
  { name: 'Nigeria Cassava', file: '/samples/nigeria-cassava.csv', count: 78 },
  { name: 'East Africa Beans', file: '/samples/east-africa-beans.csv', count: 54 },
  { name: 'Global Crops', file: '/samples/global-crops.csv', count: 52 },
];

export function VarietyMapper() {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [records, setRecords] = useState<VarietyRecord[]>([]);
  const [selectedVariety, setSelectedVariety] = useState('all');
  const [showModel, setShowModel] = useState(false);
  const [fileName, setFileName] = useState('');
  const [isSample, setIsSample] = useState(false);

  const varieties = useMemo(() => {
    const set = new Set(records.map((r) => r.variety));
    return Array.from(set).sort();
  }, [records]);

  const parseCsv = useCallback((text: string, name: string) => {
    const results = Papa.parse(text, { skipEmptyLines: true });
    const rows = results.data as string[][];
    if (rows.length < 2) return;

    const headers = rows[0];
    let lonIdx = -1;
    let latIdx = -1;
    let varIdx = -1;

    headers.forEach((h, i) => {
      const prefix = h.trim().toLowerCase().slice(0, 3);
      if (prefix === 'lon') lonIdx = i;
      else if (prefix === 'lat') latIdx = i;
      else if (prefix === 'var') varIdx = i;
    });

    if (lonIdx === -1) { toast.error(t('varietyMapper.errorNoLon')); return; }
    if (latIdx === -1) { toast.error(t('varietyMapper.errorNoLat')); return; }

    const parsed: VarietyRecord[] = [];
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      const lat = parseFloat(row[latIdx]);
      const lon = parseFloat(row[lonIdx]);
      if (isNaN(lat) || isNaN(lon)) continue;
      parsed.push({
        variety: varIdx >= 0 ? (row[varIdx] || '').trim() : 'Unknown',
        lat,
        lon,
        rowIndex: i,
      });
    }

    setRecords(parsed);
    setSelectedVariety('all');
    setShowModel(false);
    setFileName(name);
    setIsSample(false);
  }, [t]);

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      skipEmptyLines: true,
      complete(results) {
        // Re-serialize so parseCsv can handle it uniformly
        const text = Papa.unparse(results.data as string[][]);
        parseCsv(text, file.name);
      },
    });
  }

  async function loadSample(sample: typeof SAMPLE_FILES[number]) {
    try {
      const res = await fetch(sample.file);
      const text = await res.text();
      parseCsv(text, sample.name);
      setIsSample(true);
    } catch {
      toast.error('Failed to load sample file');
    }
  }

  function handleVarietyChange(value: string) {
    setSelectedVariety(value);
    if (value === 'all') setShowModel(false);
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-3xl flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          {t('varietyMapper.title')}
        </h2>
        <p className="text-spi-slate mt-1">{t('varietyMapper.description')}</p>
      </div>

      {/* Controls bar */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* File upload */}
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            onChange={handleFile}
            className="hidden"
          />
          <button
            onClick={() => fileRef.current?.click()}
            className="btn btn-primary text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            {fileName ? t('varietyMapper.changeFile') : t('varietyMapper.uploadCsv')}
          </button>

          {/* Sample file buttons */}
          {records.length === 0 && (
            <>
              <span className="text-sm text-spi-slate">or try:</span>
              {SAMPLE_FILES.map((s) => (
                <button
                  key={s.file}
                  onClick={() => loadSample(s)}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium bg-spi-parchment text-spi-slate hover:bg-spi-seed hover:text-spi-green transition-colors"
                >
                  {s.name}
                </button>
              ))}
            </>
          )}

          {fileName && (
            <span className="text-sm text-spi-slate truncate max-w-[200px] flex items-center gap-1.5">
              {fileName}
              {isSample && (
                <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold leading-none flex-shrink-0">
                  Sample
                </span>
              )}
            </span>
          )}

          {/* Variety dropdown */}
          {records.length > 0 && (
            <>
              <select
                value={selectedVariety}
                onChange={(e) => handleVarietyChange(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">{t('varietyMapper.allVarieties')}</option>
                {varieties.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>

              {/* Model checkbox */}
              <label className="flex items-center gap-2 text-sm text-spi-slate cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showModel}
                  disabled={selectedVariety === 'all'}
                  onChange={(e) => setShowModel(e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-40"
                />
                {t('varietyMapper.addModel')}
              </label>

              {/* Stats badge */}
              <span className="ml-auto text-sm text-spi-slate bg-spi-parchment px-3 py-1 rounded-full">
                {isSample && <span className="text-amber-600 font-medium">Sample &middot; </span>}
                {records.length} {t('varietyMapper.records')} &middot; {varieties.length} {t('varietyMapper.varieties')}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Map — large */}
      <div className="card overflow-hidden" style={{ height: 'calc(100vh - 280px)', minHeight: '500px' }}>
        <MapView
          records={records}
          selectedVariety={selectedVariety}
          showModel={showModel}
        />
      </div>

      {/* Data table */}
      {records.length > 0 && (
        <div className="card p-0 overflow-hidden">
          <DataTable records={records} selectedVariety={selectedVariety} />
        </div>
      )}
    </div>
  );
}
